import { BuildingOfficeIcon, UserGroupIcon, MapPinIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/solid';

export const FALLBACK_COLORS = [
  '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6',
  '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#06b6d8'
];

export const PRIMARY_COLOR = '#830bd4';

// Semantic color map for status breakdown labels
export const STATUS_COLOR_MAP = {
  completed: '#22c55e',
  'in progress': '#f59e0b',
  overdue: '#ef4444',
  closed: '#9ca3af',
  unsolicited: PRIMARY_COLOR,
  requested: '#22c55e',
  open: '#f59e0b',
  active: '#3b82f6',
  inactive: '#9ca3af',
  warning: '#f59e0b',
  expired: '#6b7280',
  complete: '#22c55e',
};

/** Get color for a status breakdown label — falls back to FALLBACK_COLORS by index */
export const getStatusColor = (label, index) => {
  return STATUS_COLOR_MAP[label.toLowerCase()] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

/** Traffic-light color: normalize value to 0-100 then green ≥70, amber 50-69, red <50 */
export const trafficLight = (normalized) => {
  if (normalized >= 70) return '#22c55e';
  if (normalized >= 50) return '#f59e0b';
  return '#ef4444';
};

/** Get bar colors — normalizes based on metric type, then applies traffic-light */
export const getBarColors = (values, metric) => {
  if (metric === 'average_rating') {
    return values.map(v => trafficLight((v / 5) * 100));
  }
  if (METRIC_MAX[metric]) {
    return values.map(v => trafficLight(v));
  }
  return values.map(() => PRIMARY_COLOR);
};

export const METRIC_LABELS = {
  completion_rate: 'Completion Rate (%)',
  average_rating: 'Average Rating',
  sentiment_score: 'Sentiment Score (%)',
  feedbacks_given: 'Feedbacks Given',
  feedbacks_received: 'Feedbacks Received',
  feedback_received: 'Feedback Received',
  avg_days: 'Average Days to Complete',
  task_count: 'Task Count',
  tasks_completed: 'Tasks Completed'
};

// Metrics where lower values are better — sorted ascending (closest to 0 on top)
export const INVERSE_METRICS = new Set(['avg_days']);

// Maximum scale for each metric so bars are visually proportional
export const METRIC_MAX = {
  completion_rate: 100,
  average_rating: 5,
  sentiment_score: 100,
};

export const NON_METRIC_KEYS = new Set(['name', 'rank']);

/** Derive a display title from the type field: uppercase first letter */
export const formatTypeTitle = (type) => {
  if (!type) return 'Stats';
  return type.charAt(0).toUpperCase() + type.slice(1);
};

export const SEGMENTS = [
  { key: 'departments', label: 'Departments', icon: BuildingOfficeIcon, filteredKey: 'filtered_departments' },
  { key: 'locations', label: 'Locations', icon: MapPinIcon, filteredKey: 'filtered_locations' },
  { key: 'users', label: 'Users', icon: UserGroupIcon, filteredKey: 'filtered_users' },
  { key: 'tasks', label: 'Tasks', icon: ClipboardDocumentListIcon, filteredKey: 'filtered_tasks' },
];


export const segmentHasData = (segmentData) => {
  if (!segmentData || typeof segmentData !== 'object') return false;
  if (Array.isArray(segmentData)) return segmentData.length > 0;
  return Object.values(segmentData).some(
    arr => Array.isArray(arr) && arr.length > 0
  );
};


export const getSegmentMetrics = (segmentData) => {
  if (!segmentData || typeof segmentData !== 'object' || Array.isArray(segmentData)) return [];
  return Object.entries(segmentData)
    .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
    .map(([metric, entries]) => ({ metric, entries }));
};
